import axios from "axios";
import CONFIG from "../../config";
import { CRED } from "../../config/cred";
import apis from "../../config/apis";
import HELPERS from "../../utils/helper";
import { ErrorMessageHandling } from "../../components/error";
import {
  getPaymentGatewayChecking,
  getPaymentType,
} from "../cart/payment";

const loadScript = (src) => {
  return new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Fetch the instalment schedule for a product
 */
export const getInstalmentSchedule = async ({
  productId,
  institute_id,
  user_id,
  student_auth,
  generateAuthHeaders,
}) => {
  let headerObj;
  if (student_auth && user_id) {
    headerObj = {
      headers: {
        "x-prod-user-id": user_id,
        "x-prod-inst-id": institute_id,
        Authorization: student_auth,
        Source: "WEB",
      },
    };
  } else if (generateAuthHeaders) {
    const customHeaders = generateAuthHeaders(institute_id);
    headerObj = {
      headers: {
        ...customHeaders,
        "x-prod-inst-id": institute_id,
        "x-prod-user-id": user_id || "",
        Source: "WEB",
      },
    };
  } else {
    headerObj = {
      headers: {
        "x-prod-inst-id": institute_id,
        "x-prod-user-id": user_id || "",
        Source: "WEB",
      },
    };
  }

  try {
    const res = await HELPERS.ApiCall(
      apis.INSTALMENT_SCHEDULE,
      { productId },
      "POST",
      "product",
      headerObj
    );
    if (res.status === 200 && res.data.status_code === 200) {
      return res.data.result;
    }
    throw new Error(res.data.message || "Failed to get installment schedule");
  } catch (err) {
    throw err;
  }
};

/**
 * Create a Razorpay order for instalment payment (subsequent payments)
 */
const createInstalmentRazorpayOrder = async ({
  amount,
  productId,
  selectedInstalments,
  institute_id,
  user_id,
  student_auth,
  access_token,
  user_info,
  user_type,
  country_id,
  c_symbol,
}) => {
  const headers = {
    headers: {
      "x-prod-user-id": user_id.toString(),
      "x-prod-inst-id": institute_id,
      Authorization: access_token?.third_party_auth_token || student_auth,
      "Content-Type": "application/json",
      Source: "WEB",
    },
  };
  const payload = {
    amount: (amount * 100).toFixed(2),
    currency: "INR",
    notes: {
      payment_for: "Product",
      instalment_numbers: selectedInstalments,
      product_id: String(productId),
      institute_id: String(institute_id),
      user_id: String(user_id),
      amount: String(amount),
      payment_source: "WEB",
      student_id:
        user_type === 99
          ? "0"
          : user_info.student_id
          ? String(user_info.student_id)
          : "0",
      country_id: String(country_id || ""),
      currency_code: String(c_symbol),
      name: String(user_info.name),
    },
  };

  const response = await axios.post(
    `${CONFIG.BASE_URL}/api/v2/onlinePayment/create_order`,
    payload,
    headers
  );
  if (response.data.statusCode === 200) {
    return response.data.result;
  }
  throw new Error(response.data.message || "Failed to create order");
};

/**
 * Update order payment detail after Razorpay success (OAuth flow)
 */
const updateInstalmentOrderPayment = async ({
  razorpay_payment_id,
  order_id,
  student_auth,
  access_token,
}) => {
  const headers = {
    Authorization: access_token?.third_party_auth_token || student_auth,
    "Content-Type": "application/json",
    Source: "WEB",
  };
  const response = await axios.post(
    `${CONFIG.BASE_URL}/api/v2/onlinePayment/update-order-payment-detail`,
    { razorpay_payment_id, order_id },
    { headers }
  );
  return response.data;
};

/**
 * Update instalment payment on the product backend after successful Razorpay payment
 */
const updateInstalmentPaymentOnBackend = async ({
  productId,
  selectedInstalments,
  razorpayPaymentId,
  razorpayOrderId,
  institute_id,
  user_id,
  student_auth,
}) => {
  const headerObj = {
    headers: {
      "x-prod-user-id": user_id.toString(),
      "x-prod-inst-id": institute_id,
      Authorization: student_auth,
      "Content-Type": "application/json",
      Source: "WEB",
    },
  };
  const payload = {
    productId,
    instalment_numbers: selectedInstalments,
    razorpayOrderId: razorpayOrderId || "",
    razorpayPaymentId,
  };
  const res = await HELPERS.ApiCall(
    apis.INSTALMENT_UPDATE_PAYMENT,
    payload,
    "POST",
    "product",
    headerObj
  );
  return res.data;
};

/**
 * Main function: initiates the full instalment payment flow for subsequent payments
 *
 * @param {Object} opts
 * @param {number} opts.productId
 * @param {number[]} opts.selectedInstalments - array of selected instalment numbers (e.g., [3, 4])
 * @param {number} opts.totalAmount - total amount for selected instalments
 * @param {Object} opts.props - component props (user_id, institute_id, student_auth, user_info, user_type, dispatch)
 * @param {Object} opts.gatewayData - from getPaymentGatewayChecking
 * @param {string} opts.public_token
 * @param {boolean} opts.is_oAuth_done
 * @param {string} opts.c_symbol - currency symbol
 * @param {number} opts.country_id
 * @param {Function} opts.onSuccess
 * @param {Function} opts.onFailure
 * @param {Function} opts.setLoader
 * @param {Function} opts.message - toast message function
 */
export const initiateInstalmentPayment = async ({
  productId,
  selectedInstalments,
  totalAmount,
  props,
  gatewayData,
  public_token,
  is_oAuth_done,
  c_symbol,
  country_id,
  onSuccess,
  onFailure,
  setLoader,
  message,
}) => {
  const {
    user_id,
    institute_id,
    student_auth,
    access_token,
    user_info,
    user_type,
  } = props;

  try {
    setLoader(true);

    const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!res) {
      message("e", "Failed to load payment gateway. Please check your network.");
      setLoader(false);
      return;
    }

    let orderResult = null;
    if (is_oAuth_done) {
      orderResult = await createInstalmentRazorpayOrder({
        amount: totalAmount,
        productId,
        selectedInstalments,
        institute_id,
        user_id,
        student_auth,
        access_token,
        user_info,
        user_type,
        country_id,
        c_symbol,
      });

      if (!orderResult?.razorpay_order_id) {
        message("e", "Payment order could not be created.");
        setLoader(false);
        return;
      }
    }

    const notes = {
      payment_for: "Product",
      product_id: String(productId),
      instalment_numbers: selectedInstalments,
      institute_id: String(institute_id),
      user_id: String(user_id),
      amount: String(totalAmount),
      payment_source: "WEB",
      country_id: String(country_id || ""),
      currency_code: String(c_symbol),
      name: String(user_info.name),
      payment_gateway_account_type: "Proctur",
      payment_gateway_name: "Razorpay",
    };

    const options = {
      key: is_oAuth_done ? public_token : CRED.RAZORPAY_KEY,
      amount: (Number(totalAmount) * 100).toFixed(2),
      currency: "INR",
      name: "Installment Payment",
      description: `Installments ${selectedInstalments.join(", ")}`,
      handler: async (response) => {
        try {
          if (is_oAuth_done) {
            await updateInstalmentOrderPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              order_id: orderResult.order_id,
              student_auth,
              access_token,
            });
          }
          message("s", "Installment payment successful!");
          if (onSuccess) onSuccess();
        } catch (err) {
          message("e", "Payment recorded but update failed. Please contact support.");
          if (onFailure) onFailure(err);
        }
        setLoader(false);
      },
      theme: {
        color: getComputedStyle(document.documentElement).getPropertyValue(
          "--theme-color"
        ),
      },
      notes,
      modal: {
        ondismiss: () => {
          setLoader(false);
        },
      },
    };

    if (is_oAuth_done && orderResult?.razorpay_order_id) {
      options.order_id = orderResult.razorpay_order_id;
    }

    setLoader(false);
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (response) => {
      message("e", "Payment failed. Please try again.");
      if (onFailure) onFailure(response);
    });
    rzp.open();
  } catch (error) {
    setLoader(false);
    const errMsg =
      error?.response?.data?.message || error.message || "Payment failed";
    message("e", errMsg);
    if (onFailure) onFailure(error);
  }
};
